import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Dropdown, DropdownItem } from '@amzn/storm-ui';
import { CustomAlert } from '../../../UiComponentsLibrary/CustomAlert/CustomAlert';
import { URLS } from '../../constants/url';
import { UserRoles } from '../../constants/userRoles';
import {
  SET_SELECTED_ART_EVENT_ID,
  SET_SELECTED_ART_EVENT_STATUS,
} from '../../store/actions/types';
import { useAppContext } from '../../store/appContext';
import {
  AlertWrapper,
  ArtEventDropdownContainer,
  StyledInlineAlert,
} from './ArtEventDropdown.styles';
import { ART_EVENT_ID_LOCAL_STORAGE_KEY } from '../../constants/artEvent/artEventConstants';
import {
  getFromLocalStorage,
  setToLocalStorage,
} from '../../utils/localStorage';
import { useGetArtEvents } from '../../../EditArtEvent/hooks/useGetArtEvents';
import { ART_EVENT_STATUS } from '../../../EditArtEvent/constants/artEventStatus';

const ArtEventDropdown = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { state, dispatch } = useAppContext();
  const [dropdownValue, setDropdownValue] = useState<string>('');
  const hasRedirectedRef = useRef<boolean>(false);

  const isAdminOrSuperAdmin =
    state.currentUser?.role === UserRoles.ADMIN ||
    state.currentUser?.role === UserRoles.SUPER_ADMIN;

  const { data: artEventsData, isLoading, isError } = useGetArtEvents();

  // Filter out deleted art events
  const activeArtEvents =
    artEventsData?.artEvents?.filter(
      (event) => event.status !== ART_EVENT_STATUS.DELETED
    ) || [];

  const artEventIDs = activeArtEvents.map((event) => event.artEventID);
  const hasNoArtEvents = !isLoading && !isError && artEventIDs.length === 0;

  // Helper function to get status for an art event ID
  const getArtEventStatus = useCallback(
    (artEventID: string): string | null => {
      const artEvent = activeArtEvents.find(
        (event) => event.artEventID === artEventID
      );
      return artEvent?.status || null;
    },
    [activeArtEvents]
  );

  useEffect(() => {
    if (isError) {
      dispatch({
        type: SET_SELECTED_ART_EVENT_ID,
        payload: undefined,
      });
      dispatch({
        type: SET_SELECTED_ART_EVENT_STATUS,
        payload: undefined,
      });
    }
  }, [isError, dispatch]);

  useEffect(() => {
    if (hasNoArtEvents) {
      dispatch({
        type: SET_SELECTED_ART_EVENT_ID,
        payload: '',
      });
      dispatch({
        type: SET_SELECTED_ART_EVENT_STATUS,
        payload: null,
      });
      if (isAdminOrSuperAdmin && !hasRedirectedRef.current) {
        hasRedirectedRef.current = true;
        navigate(URLS.CREATE_ART_EVENT);
      }
    }
  }, [hasNoArtEvents, isAdminOrSuperAdmin, navigate, dispatch]);
  useEffect(() => {
    if (artEventIDs.length > 0 && !isLoading && !isError) {
      const savedArtEventId = getFromLocalStorage(
        ART_EVENT_ID_LOCAL_STORAGE_KEY
      );
      const validSavedId =
        savedArtEventId && artEventIDs.includes(savedArtEventId);
      const selectedId = validSavedId ? savedArtEventId : artEventIDs[0];

      if (selectedId !== dropdownValue) {
        setDropdownValue(selectedId);
        setToLocalStorage(ART_EVENT_ID_LOCAL_STORAGE_KEY, selectedId);
        dispatch({
          type: SET_SELECTED_ART_EVENT_ID,
          payload: selectedId,
        });
        dispatch({
          type: SET_SELECTED_ART_EVENT_STATUS,
          payload: getArtEventStatus(selectedId),
        });
      }
    }
  }, [
    artEventIDs,
    dropdownValue,
    isLoading,
    isError,
    dispatch,
    getArtEventStatus,
  ]);

  const handleDropdownChange = (newDropdownValue: string) => {
    setDropdownValue(newDropdownValue);
    setToLocalStorage(ART_EVENT_ID_LOCAL_STORAGE_KEY, newDropdownValue);
    dispatch({
      type: SET_SELECTED_ART_EVENT_ID,
      payload: newDropdownValue,
    });
    dispatch({
      type: SET_SELECTED_ART_EVENT_STATUS,
      payload: getArtEventStatus(newDropdownValue),
    });
  };

  const getPlaceholder = () => {
    if (isLoading) return t('loading');
    if (isError || hasNoArtEvents) return t('select-art-event');
    return t('select-art-event');
  };

  return (
    <>
      <ArtEventDropdownContainer id="art-event-dropdown-container">
        <Dropdown
          id="art-event-dropdown"
          label={t('art-event-label')}
          selectedValue={dropdownValue}
          onChange={handleDropdownChange}
          fullWidth={true}
          disabled={isLoading || isError || hasNoArtEvents}
          onOverrideLabel={(dropdownItem, value) => {
            if (value) {
              return dropdownItem;
            }
            return getPlaceholder();
          }}
        >
          {artEventIDs.map((artEventID) => (
            <DropdownItem
              id={`art-event-option-${artEventID}`}
              value={artEventID}
              key={artEventID}
            >
              {artEventID}
            </DropdownItem>
          ))}
        </Dropdown>

        {isError && (
          <AlertWrapper>
            <CustomAlert
              variant="error"
              alertMessage={t('error-loading-art-events')}
            />
          </AlertWrapper>
        )}
        {hasNoArtEvents && (
          <StyledInlineAlert
            id="no-art-events-alert"
            variant="info"
            message={t('no-art-events-available')}
          />
        )}
      </ArtEventDropdownContainer>
    </>
  );
};

export default ArtEventDropdown;
